import PropTypes from "prop-types";
import { useClientContext } from "../../ClientContext.jsx";
import { Card, STitle } from "../adminUtils.jsx";
import { RADIUS } from "../../shared/theme.js";

export default function PreviewSection({
  importState,
  importBroker,
  importFileObj,
  importParsed,
  importWarnings,
  handleImportReset,
}) {
  const { T } = useClientContext();

  if (importState !== "preview" && importState !== "saving" && importState !== "done") {
    return null;
  }

  if (!importParsed) return null;

  return (
    <Card T={T} style={{ marginBottom: 20 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <STitle
          title={`Preview — ${importBroker.toUpperCase().replace("_PACTUAL", " Pactual")}`}
          sub={`Arquivo: ${importFileObj?.name || "–"} · ${(importFileObj?.size / 1024).toFixed(0)} KB`}
          T={T}
        />
        {importState !== "saving" && (
          <button
            onClick={handleImportReset}
            style={{
              padding: "6px 14px",
              borderRadius: RADIUS.sm,
              border: `1px solid ${T.border}`,
              background: "transparent",
              color: T.sub,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            ✕ Cancelar
          </button>
        )}
      </div>

      {/* Cards de resumo — Informe IR */}
      {importParsed.type === "informe_ir" ? (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18 }}>
          {[
            {
              label: "Tipo",
              v: importParsed.report_type === "previdencia" ? "Previdência" : "Investimentos",
              color: "#7C3AED",
            },
            {
              label: "Ano-calendário",
              v: importParsed.ano_referencia ? String(importParsed.ano_referencia) : "–",
              color: "#2563EB",
            },
            {
              label: "Rend. Isentos",
              v: importParsed.rendimentos_isentos
                ? `R$ ${importParsed.rendimentos_isentos.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                : "–",
              color: "#16A34A",
            },
            {
              label: "Trib. Exclusiva",
              v: importParsed.tributacao_exclusiva
                ? `R$ ${importParsed.tributacao_exclusiva.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                : "–",
              color: "#EA580C",
            },
            {
              label: "Rend. Tributáveis",
              v: importParsed.rendimentos_tributaveis
                ? `R$ ${importParsed.rendimentos_tributaveis.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                : "–",
              color: "#DC2626",
            },
            {
              label: "Ativos detalhados",
              v: importParsed.items?.length ? `${importParsed.items.length} itens` : "–",
              color: "#0891B2",
            },
            ...(importParsed.report_type === "previdencia"
              ? [
                  {
                    label: "Saldo VGBL",
                    v: importParsed.saldo_vgbl
                      ? `R$ ${importParsed.saldo_vgbl.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                      : "–",
                    color: "#16A34A",
                  },
                  {
                    label: "Contrib. PGBL",
                    v: importParsed.contribuicao_pgbl
                      ? `R$ ${importParsed.contribuicao_pgbl.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                      : "–",
                    color: "#7C3AED",
                  },
                ]
              : []),
          ]
            .filter((s) => s.v !== "–")
            .map((s) => (
              <div
                key={s.label}
                style={{
                  background: T.bg,
                  border: `1px solid ${T.border}`,
                  borderRadius: RADIUS.md,
                  padding: "10px 16px",
                  minWidth: 160,
                  flex: "1",
                }}
              >
                <div style={{ fontSize: 11, color: T.sub, marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: s.color }}>{s.v}</div>
              </div>
            ))}
        </div>
      ) : (
        /* Cards de resumo — Extrato normal */
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18 }}>
          {[
            {
              label: "Patrimônio detectado",
              v: importParsed.patrimonio_total
                ? `R$ ${importParsed.patrimonio_total.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                : importParsed.patrimonio_usd
                  ? `$ ${importParsed.patrimonio_usd.toLocaleString("en-US", { minimumFractionDigits: 2 })} USD`
                  : "–",
              color: "#16A34A",
            },
            {
              label: "Ativos XP",
              v: importParsed.assets_xp
                ? Object.values(importParsed.assets_xp).flat().length + " posições"
                : "–",
              color: "#2563EB",
            },
            {
              label: "Ativos BTG",
              v: importParsed.assets_btg?.length ? `${importParsed.assets_btg.length} CDBs` : "–",
              color: "#7C3AED",
            },
            {
              label: "Ativos Inter",
              v: importParsed.assets_inter?.length
                ? `${importParsed.assets_inter.length} posições`
                : "–",
              color: "#EA580C",
            },
            {
              label: "Posições Avenue",
              v:
                importParsed.positions?.length || importParsed.positions_avenue?.length
                  ? `${(importParsed.positions || importParsed.positions_avenue || []).length} posições`
                  : "–",
              color: "#16A34A",
            },
            {
              label: "Retorno mês",
              v:
                importParsed.ret_month != null
                  ? `${importParsed.ret_month.toFixed(2)}%`
                  : "não encontrado",
              color: importParsed.ret_month != null ? "#0891B2" : "#94A3B8",
            },
          ]
            .filter((s) => s.v !== "–")
            .map((s) => (
              <div
                key={s.label}
                style={{
                  background: T.bg,
                  border: `1px solid ${T.border}`,
                  borderRadius: RADIUS.md,
                  padding: "10px 16px",
                  minWidth: 160,
                  flex: "1",
                }}
              >
                <div style={{ fontSize: 11, color: T.sub, marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: s.color }}>{s.v}</div>
              </div>
            ))}
        </div>
      )}

      {/* Alertas / warnings */}
      {importWarnings.length > 0 && (
        <div
          style={{
            background: "#FFFBEB",
            border: "1px solid #FDE68A",
            borderRadius: 10,
            padding: "10px 14px",
            marginBottom: 16,
          }}
        >
          {importWarnings.map((w, i) => (
            <div key={i} style={{ fontSize: 12, color: "#92400E", marginBottom: 2 }}>
              ⚠️ {w}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

PreviewSection.propTypes = {
  importState: PropTypes.string.isRequired,
  importBroker: PropTypes.string.isRequired,
  importFileObj: PropTypes.object,
  importParsed: PropTypes.object,
  importWarnings: PropTypes.array.isRequired,
  handleImportReset: PropTypes.func.isRequired,
};
